<template>
  <div class="growth-wizard getting-started-wizard">
    <template v-if="showCompletionScreen">
      <GrowthWizardCompletionScreen
        :organization="organization"
        :completion-screen-data="completionScreen.data"
        :completion-screen-template="completionScreen.template"
      />
    </template>
    <WelcomeScreen
      v-else-if="welcomeScreenVisible"
      @start="startWizard"
    />
    <template v-else>
      <div
        id="growth-wizard-section-steps"
        class="growth-wizard-section"
      >
        <div id="growth-wizard-header">
          <p class="title">👋 Let's get you started with Hubstaff!</p>
        </div>
        <div class="gs-steps wizard-progress-bar">
          <div class="progress-bar-item">
            <div
              class="progress-fill"
              :style="{ width: `${overallProgressPercentage}%` }"
            ></div>
          </div>
        </div>
      </div>
      <div
        v-loading="loading"
        class="loading static-curtain"
      />
      <div
        v-if="substepData"
        id="growth-wizard-section-substeps"
        class="growth-wizard-section"
      >
        <component
          :is="currentSubstepComponent"
          class="substep-component"
          v-bind="getSubstepProps()"
          v-on="getSubstepEvents()"
        />
      </div>

      <div
        v-if="substepData"
        class="streamlined-bottom-bar"
      >
        <div class="cta-wrapper">
          <div class="ctas-left">
            <AppButton
              v-if="showExploreOnMyOwnButton"
              class="hs-btn-outline-gray"
              @click="exploreOnMyOwn"
            >
              <i class="hi hi-16 hi-exit"></i>
              Explore on my own
            </AppButton>
          </div>

          <div class="ctas-right">
            <AppButton
              v-if="!isFirstSubstep"
              class="back-button hs-btn-outline-gray"
              @click="returnToPreviousStep"
            >
              <i class="hi hi-16 hi-left-arrow"></i>
              Back
            </AppButton>

            <AppButton
              v-if="allowSkipToNextStep"
              class="skip-button hs-btn-outline-gray"
              @click="skipToNextStep"
            >
              I'll do this later
            </AppButton>

            <AppButton
              v-tooltip="showContinueTooltip ? { placement: 'top' } : false"
              :title="continueTooltip"
              type="primary"
              class="continue-button"
              data-testid="continue-to-next-step"
              :disabled="!nextStepAllowed"
              @click="continueToNextStep($event)"
            >
              {{ nextStepText }}
              <i
                v-if="!isLastSubstep"
                class="hi hi-16 hi-right-arrow"
              ></i>
            </AppButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { GettingStartedResource } from '~/resources';
import { SUBSTEP_COMPONENTS } from '~/components/growth_wizard/getting_started/substeps/mappings';
import WizardBaseMixin from '~/mixins/GrowthWizard/WizardBaseMixin';
import GrowthWizardCompletionScreen from '~/components/growth_wizard/completion_screens/GrowthWizardCompletionScreen.vue';
import WelcomeScreen from '~/components/growth_wizard/getting_started/WelcomeScreen.vue';

export default {
  name: 'GettingStartedIndex',
  components: {
    GrowthWizardCompletionScreen,
    WelcomeScreen,
  },
  mixins: [WizardBaseMixin],
  props: {
    showWelcomeScreen: {
      type: Boolean,
      default: false,
    },
    userGettingStartedV2: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      substepsWithFinishedHandlers: [
        'set_up_project',
        'add_a_holiday',
        'set_up_timesheet_approvals',
        'set_up_time_limits',
      ],
      isSidebarCollapsed: false,
      sidebarObserver: null,
      userDismissedWelcome: false,
    };
  },
  computed: {
    substepComponentsMap() {
      return SUBSTEP_COMPONENTS;
    },
    resourceAPI() {
      return GettingStartedResource;
    },
    showExploreOnMyOwnButton() {
      return this.isSidebarCollapsed && !this.userGettingStartedV2;
    },
    welcomeScreenVisible() {
      return this.showWelcomeScreen && !this.userDismissedWelcome;
    },
    isLastSubstep() {
      return !this.nextSubstep;
    },
    nextStepText() {
      return this.nextSubstep ? 'Continue' : 'Complete onboarding';
    },
  },
  mounted() {
    this.setupSidebarObserver();
  },
  beforeUnmount() {
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
  beforeMount() {
    this.getSubstepData();
  },
  methods: {
    startWizard() {
      this.userDismissedWelcome = true;
      // The server skips Step Viewed while the Welcome screen is up, so the first step is tracked here.
      this.trackWizardEvent('Step Viewed', {
        expected_sequence: Object.fromEntries(this.allSubsteps.map((substep, index) => [index + 1, substep.id])),
      });
    },
    setupSidebarObserver() {
      const sidebarEl = document.querySelector('.hotwired-sidebar');

      if (sidebarEl) {
        this.isSidebarCollapsed = sidebarEl.getAttribute('data-collapsed') === 'true';

        this.sidebarObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-collapsed') {
              this.isSidebarCollapsed = mutation.target.getAttribute('data-collapsed') === 'true';
            }
          }
        });

        this.sidebarObserver.observe(sidebarEl, { attributes: true, attributeFilter: ['data-collapsed'] });
      }
    },
  },
};
</script>
